import { useCallback, useState } from 'react'

import { admin } from '../../lib/api/admin.ts'
import { useTelemetrySource } from '../../lib/api/source.tsx'
import type { AgentDirectoryItem, AgentDirectoryPage, TenantDirectory } from '../../lib/api/admin.ts'
import { agentStateLabel } from '../../lib/agents/state.ts'
import { formatDayClock } from '../../lib/format.ts'
import { useDebounced } from '../../lib/useDebounced.ts'
import { useResource } from '../../lib/useResource.ts'
import { Badge } from '../primitives/Badge.tsx'
import { SearchInput, SelectField } from '../primitives/Field.tsx'
import { LoadingRow } from '../primitives/States.tsx'

/**
 * Os dois seletores custam zero: vêm de fotografias em memória, não do banco
 * (§2.2). `stale: true` significa que a última atualização do diretório falhou —
 * marque a lista como defasada e não esconda nada, mesmo tratamento do
 * `reference_stale` do painel.
 */

/** Selo compacto de defasagem do diretório em memória. */
function StaleMark({ stale, capturedAt }: { stale: boolean; capturedAt: string }) {
  if (!stale) return null
  return (
    <Badge tone="warn" dot title={`Última fotografia às ${formatDayClock(capturedAt)} UTC`}>
      diretório defasado
    </Badge>
  )
}

export function TenantSelect({
  value,
  onChange,
  className = '',
}: {
  value: string
  onChange: (tenantId: string) => void
  className?: string
}) {
  const fetcher = useCallback((signal: AbortSignal) => admin.tenants(signal), [])
  const { data } = useResource<TenantDirectory>(fetcher)

  const options = [
    { value: '', label: 'Todas as empresas' },
    ...(data?.items ?? []).map((tenant) => ({ value: tenant.tenant_id, label: tenant.name })),
  ]

  return (
    <div className={`flex items-end gap-2 ${className}`}>
      <SelectField
        label="Empresa"
        value={value}
        onValueChange={onChange}
        options={options}
        className="w-56"
      />
      {data !== null && <StaleMark stale={data.stale} capturedAt={data.captured_at} />}
    </div>
  )
}

/**
 * Busca incremental de agente. Pode disparar a cada tecla sem culpa — o servidor
 * responde da memória —, mas `useDebounced` entra por causa do render, não do
 * servidor (§2.2). Selecionar leva à investigação de um agente só (§2.1, §2.3),
 * onde nada é amostrado.
 */
export function AgentPicker({
  tenantId,
  onPick,
}: {
  tenantId?: string
  onPick: (item: AgentDirectoryItem) => void
}) {
  const source = useTelemetrySource()
  const [search, setSearch] = useState('')
  const debounced = useDebounced(search)

  // Na fonte de cliente o `tenantId` é descartado: a empresa vem do token.
  const fetcher = useCallback(
    (signal: AbortSignal) =>
      source.agents({ search: debounced, tenantId, pageSize: 20 }, signal),
    [source, debounced, tenantId],
  )
  const { data, loading } = useResource<AgentDirectoryPage>(fetcher)

  return (
    <div className="min-w-0 flex-1">
      <SearchInput
        label="Buscar agente"
        placeholder={source.multiTenant ? 'hostname ou empresa' : 'hostname'}
        value={search}
        onValueChange={setSearch}
        className="w-72"
      />

      {data !== null && (
        <div className="mt-1 max-h-72 overflow-y-auto border border-line-subtle bg-surface">
          {data.items.length === 0 ? (
            <p className="px-2 py-2 text-sm text-fg-muted">
              {debounced === '' ? 'Digite para buscar um agente.' : 'Nenhum agente corresponde.'}
            </p>
          ) : (
            <ul>
              {data.items.map((item) => (
                <li key={item.endpoint_id}>
                  <button
                    type="button"
                    onClick={() => onPick(item)}
                    className="flex w-full items-baseline justify-between gap-3 px-2 py-1 text-left text-sm hover:bg-inset"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-fg">
                        {item.hostname ?? item.endpoint_id}
                      </span>
                      <span className="block truncate text-micro text-fg-muted">
                        {source.multiTenant
                          ? (item.tenant_name ?? 'empresa não resolvida')
                          : agentStateLabel(item.state)}
                        {item.agent_version !== null && ` · v${item.agent_version}`}
                      </span>
                    </span>
                    <span className="tnum shrink-0 text-micro text-fg-muted">
                      {item.last_seen_at === null ? 'nunca visto' : formatDayClock(item.last_seen_at)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between gap-2 border-t border-line-subtle px-2 py-1 text-micro text-fg-muted">
            <span>
              {loading ? 'buscando…' : `${data.items.length} de ${data.total_count}`}
            </span>
            <StaleMark stale={data.stale} capturedAt={data.captured_at} />
          </div>
        </div>
      )}
      {data === null && loading && <LoadingRow label="Carregando agentes…" />}
    </div>
  )
}
